import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardMedia from '@material-ui/core/CardMedia';
import config from 'shared/config';
import React from 'react';
import { formatCurrency } from 'shared/utils/htmlHelpers';
import { PropTypes } from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

const styles = theme => ({
	productCardStyle: {
		position: 'relative',
		height: '100%',
	},

	cardMediaStyle: {
		height: '100%',
		backgroundSize: 'contain',
	},

	overlayStyle: {
		backgroundColor: theme.palette.action.active,
		position: 'absolute',
		bottom: '0',
		right: '0',
		left: '0',
		padding: '0.1em 0.3em 0.2em',
		display: 'block',
	},

	titleStyle: {
		color: theme.palette.getContrastText(theme.palette.action.active),
		opacity: 0.87,
		display: 'block',
		marginRight: '10px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		lineHeight: '1.5em',
		fontSize: '0.8em',
		transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',

		[theme.breakpoints.up('lg')]: {
			fontSize: '0.9em',
		},

		[theme.breakpoints.up('xl')]: {
			fontSize: '1em',
		},
	},

	priceStyle: {
		color: theme.palette.getContrastText(theme.palette.action.active),
		opacity: 0.54,
		fontSize: '1em',
		lineHeight: '1.5em',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},

	subtitleStyleMsrp: {
		textDecoration: 'line-through',
		color: theme.palette.getContrastText(theme.palette.action.active),
		opacity: 0.54,
	},

	subtitleSku: {
		color: theme.palette.getContrastText(theme.palette.action.active),
		opacity: 0.54,
		fontSize: '1em',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		marginLeft: 'auto',
	},

	subtitleStyle: {
		lineHeight: '1.5em',
	},

	cardSubtitleFlex: {
		display: 'flex',
	},

	cardSubtitleMsrp: {
		textDecoration: 'line-through',
		paddingLeft: '0.5em',
	},
});

/**
 * Display a product as a card.
 */
const ProductCard = ({
	name,
	price,
	oldPrice,
	msrp,
	pictureUrl,
	handleNav,
	isFetching,
	sku,
	classes,
}) => {
	const { ShowCurrentPrice, ShowMsrp, ShowPriceDecimal, ShowSku } = config.PRODUCT;

	const skuDisplayValue = sku.match(/^PF\*/i) ? <br /> : sku;

	// Display currentPrice as main price if ShowCurrentPrice is true AND currentPrice is not 0/null/undefined/NaN
	// Display MSRP as main price if currentPrice is not used AND ShowMsrp is true AND MSRP is not 0/null/undefined/NaN
	// In all other cases, display nothing.
	const mainPrice = (ShowCurrentPrice && price) || (ShowMsrp && msrp);

	// if second parameter is true, shows decimals in price, otherwise rounds down to nearest dollar
	const formatPrice = formatCurrency(mainPrice, ShowPriceDecimal);

	// Display scratched out price if:
	// msrp exists (not undefined/null/NaN) and is not 0
	// msrp is greater than (and not equal to) mainPrice
	// ShowCurrentPrice and ShowMsrp are both true
	const formatSecondaryPrice =
		// eslint-disable-next-line no-nested-ternary
		ShowMsrp && msrp
			? msrp > mainPrice && formatCurrency(msrp, ShowPriceDecimal)
			: ShowCurrentPrice && oldPrice
				? oldPrice > mainPrice && formatCurrency(oldPrice, ShowPriceDecimal)
				: null;

	return isFetching ? null : (
		<Card onClick={handleNav} className={classes.productCardStyle}>
			<CardMedia className={classes.cardMediaStyle} image={pictureUrl}>
				<CardHeader
					title={<span className={classes.titleStyle}>{name}</span>}
					className={classes.overlayStyle}
					subheader={
						<span className={classes.cardSubtitleFlex}>
							{mainPrice ? (
								<span style={{ marginRight: '20px' }}>
									<span
										className={classes.priceStyle}
										style={
											oldPrice && oldPrice > mainPrice
												? { color: config.COLORS.ProductCategoryCardSalePrice, opacity: 1 }
												: null
										}
									>
										{formatPrice}
									</span>
									{formatSecondaryPrice ? (
										<span className={classes.cardSubtitleMsrp}>
											<span className={classes.subtitleStyleMsrp}>{formatSecondaryPrice}</span>
										</span>
									) : null}
								</span>
							) : null}
							{ShowSku ? (
								<span className={classes.subtitleSku}>
									<span className={classes.subtitleStyle}>{skuDisplayValue}</span>
								</span>
							) : null}
						</span>
					}
				/>
			</CardMedia>
		</Card>
	);
};

ProductCard.propTypes = {
	classes: PropTypes.shape({
		overlayStyle: PropTypes.string,
		titleStyle: PropTypes.string,
		subtitleStyle: PropTypes.string,
		subtitleSku: PropTypes.string,
		subtitleStyleMsrp: PropTypes.string,
		productCardStyle: PropTypes.string,
		cardMediaStyle: PropTypes.string,
	}).isRequired,
	isFetching: PropTypes.bool.isRequired,
	handleNav: PropTypes.func.isRequired,
	name: PropTypes.string.isRequired,
	price: PropTypes.number.isRequired,
	oldPrice: PropTypes.number,
	msrp: PropTypes.number.isRequired,
	pictureUrl: PropTypes.string.isRequired,
	sku: PropTypes.string.isRequired,
};

ProductCard.defaultProps = {
	oldPrice: 0,
};

export default withStyles(styles, { name: 'ProductCard' })(ProductCard);
